/**
 * `useSignedInUser` — adapt the session-stored user payload (mirrors
 * the `LoginResponse.user` blob process-runner writes via `setUserInfo`)
 * to the mobile app's `User` shape so HomeHeader/Avatar/ProfilePanel
 * render the actual signed-in user. Falls back to the stub when no
 * session is present (e.g. running the dev/showroom in isolation).
 */

import { useEffect, useState } from 'react'
import { getSignedUser, getTenantDetails, isValidSession } from '@services/session'
import type { User } from '../types/user'
import { stubUser } from '../auth/stubSession'

function deriveName(
  signed: ReturnType<typeof getSignedUser>,
  emailFallback: string,
): string {
  const first = (signed.firstName as string | undefined)?.trim()
  const last = (signed.lastName as string | undefined)?.trim()
  if (first && last) return `${first} ${last}`
  if (first) return first
  if (signed.userName) return String(signed.userName)
  return emailFallback
}

export function useSignedInUser(): User {
  const [user, setUser] = useState<User>(() => mapUser())

  // Re-read on storage events so a sibling tab's login surfaces here.
  useEffect(() => {
    function refresh() {
      setUser(mapUser())
    }
    window.addEventListener('storage', refresh)
    return () => window.removeEventListener('storage', refresh)
  }, [])

  return user
}

function mapUser(): User {
  if (!isValidSession()) return stubUser
  const signed = getSignedUser()
  const tenant = getTenantDetails()
  const email = (signed.email as string | undefined) ?? tenant?.email ?? ''
  const name = deriveName(signed, email || 'You')
  const roles = Array.isArray(signed.roles)
    ? signed.roles.filter((role): role is string => typeof role === 'string')
    : undefined
  return {
    ...signed,
    id: (signed.id as string | undefined) ?? (signed.userName as string | undefined) ?? email ?? 'me',
    name,
    email: email || undefined,
    avatarUrl: (signed.picture as string | undefined) ?? undefined,
    role:
      (signed.role as string | undefined) ??
      roles?.[0] ??
      (signed.userType as string | undefined) ??
      undefined,
    department:
      (signed.departmentName as string | undefined) ??
      (signed.department as string | undefined) ??
      undefined,
    tenantId: tenant?.tenantId ?? undefined,
  }
}
